import React, { useState, useEffect, useRef } from "react";

import { 
  Campaign, 
  CampaignNPC, 
} from "@dndModels";

import DandDAPI from "@dndUtilities/system_api";
import { makeStyles } from "@utilities/theme_utilities";

import EditableLabel from "@components/input/strings/EditableLabel";
import DeleteButton from "@components/input/buttons/DeleteButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import FlexBox from "@components/layout/FlexBox";
import VendorServicesInput from "./VendorServicesInput";
import VendorInventoryInput from "./VendorInventoryInput";
import StringBox from "@components/input/strings/StringBox";

interface Props {
  campaign: Campaign;
  npc: CampaignNPC;
  onDelete: () => void;
}

const api = DandDAPI.getInstance();

export default function CampaignNPCInput({ campaign, npc, onDelete }: Props) {
  const [reloading, setReloading] = useState(false);
  // const [processing, setProcessing] = useState(false);
  const [ping, setPing] = useState(false);
  const [open, setOpen] = useState(npc.name.length === 0);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={open}
        full_page
        onClose={() => { setOpen(false); }}>
        { renderInputControls() }
      </ModalOrDrawer>
    )
  }

  const renderInputControls = () => {
    return (
      <FlexBox direction="column">
        <FlexBox direction="row">
          <EditableLabel
            name="Name"
            value={npc.name}
            fontSize={24}
            onChange={ async (changed: string) => {
              npc.name = changed;
              api.upsertObject("campaign_npc", npc);
              setPing(!ping);
            }}
          />
        </FlexBox>
        <FlexBox>
          <StringBox
            name="Appearance"
            value={npc.appearance}
            multiline
            onChange={(changed: string) => {
              npc.appearance = changed;
              api.upsertObject("campaign_npc", npc);
              setPing(!ping);
            }}
          />
        </FlexBox>
        <FlexBox>
          <StringBox
            name="Voice"
            value={npc.voice}
            multiline
            onChange={(changed: string) => {
              npc.voice = changed;
              api.upsertObject("campaign_npc", npc);
              setPing(!ping);
            }}
          />
        </FlexBox>
        <FlexBox>
          <StringBox
            name="Details"
            value={npc.description}
            multiline
            onChange={(changed: string) => {
              npc.description = changed;
              api.upsertObject("campaign_npc", npc);
              setPing(!ping);
            }}
          />
        </FlexBox>
        <FlexBox>
          <StringBox
            name="Will Buy"
            value={npc.will_buy}
            multiline
            onChange={(changed: string) => {
              npc.will_buy = changed;
              api.upsertObject("campaign_npc", npc);
              setPing(!ping);
            }}
          />
        </FlexBox>
        <FlexBox direction="row">
          <VendorServicesInput
            campaign={campaign}
            npc={npc}
          />
          <VendorInventoryInput
            campaign={campaign}
            npc={npc}
          />
        </FlexBox>
      </FlexBox>
    );
  }

  return (
    <div className="padded">
      <FlexBox direction="row" className={cx(classes.root)}>
        <FlexBox className="header centered clickable" onClick={() => setOpen(true) }>{ npc.name }</FlexBox>
        <FlexBox xs_expand={0}>
          <div style={{ float: "right"}}>
            <DeleteButton
              no_fab
              confirm_message={`Are you sure you want to delete ${npc.name}? This cannot be undone.`}
              onClick={onDelete}
            />
          </div>
        </FlexBox>
      </FlexBox>
      { renderExtras() }
    </div>
  );
}
